import useInView from '../hooks/useInView';

const STEPS = [
  {
    number: '01',
    title: 'Deep Role Alignment',
    description: 'We begin with a structured intake to fully understand the role, team dynamics, culture, and success metrics — not just the job description.',
  },
  {
    number: '02',
    title: 'Intelligent Shortlisting',
    description: 'AI-assisted screening combined with human judgment. We evaluate candidates on skills, motivation, risks, and actual fit — not keyword matching.',
  },
  {
    number: '03',
    title: 'Insight-Driven Submissions',
    description: 'You receive candidates with detailed insight reports covering their strengths, motivations, potential risks, and why they fit — not just a resume.',
  },
  {
    number: '04',
    title: 'Funnel Optimization',
    description: 'We track every stage of the hiring funnel and provide data-driven recommendations to improve offer acceptance and reduce time-to-close.',
  },
];

export default function ProcessSection() {
  const [ref, inView] = useInView();

  return (
    <section id="process" className="py-24 bg-white" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-6 h-px bg-[#E8500A]" />
            <span className="text-[#E8500A] text-xs font-semibold uppercase tracking-[0.22em]" style={{ fontFamily: '"Inter",sans-serif' }}>
              How We Work
            </span>
          </div>
          <h2
            className="text-4xl sm:text-5xl font-black text-[#0A1628] leading-none tracking-tight"
            style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
            data-testid="process-heading"
          >
            A Process Built for<br />Precision Hiring.
          </h2>
        </div>

        {/* Steps grid */}
        <div ref={ref} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-black/8">
          {STEPS.map((step, i) => (
            <div
              key={i}
              className="relative p-8 border-r last:border-r-0 border-black/8 bg-white group hover:bg-[#F9FAFB] transition-colors duration-300"
              style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(28px)', transition: `all 0.65s ease ${i * 130}ms` }}
              data-testid={`process-step-${i}`}
            >
              {/* Step number */}
              <div
                className="text-5xl font-black text-[#E8500A] mb-6 leading-none"
                style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
              >
                {step.number}
              </div>

              {/* Connector line (hidden on last) */}
              {i < 3 && (
                <div className="absolute top-[3.5rem] right-0 translate-x-1/2 w-4 h-px bg-[#E8500A]/30 hidden lg:block" />
              )}

              <h3
                className="text-base font-black text-[#0A1628] mb-3 leading-tight"
                style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
              >
                {step.title}
              </h3>
              <p className="text-sm text-[#4B5563] leading-relaxed" style={{ fontFamily: '"Inter",sans-serif' }}>
                {step.description}
              </p>

              {/* Bottom accent */}
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E8500A] group-hover:w-full transition-all duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
